/**
 * DebugSystem — visualises server-side collision shapes as wireframe outlines
 * on the ground plane.
 *
 * Shapes match ArenaRoom.ts collision code:
 *   machines      → circle, radius 0.9 (TANK_RADIUS)
 *   destructibles → AABB or circle depending on type, halfSize = scale*0.5
 *   bullets       → swept line-segment (shown as a small circle)
 *
 * Toggle with the B key.
 */
import * as pc from 'playcanvas';
import { DESTRUCTIBLE_DEFS, type DestructibleType } from '../shared';
import type { MachineEntity } from '../entities/MachineEntity';
import type { DestructibleEntity } from '../entities/Destructible';
import type { BulletPool } from '../entities/Bullet';

// Must match ArenaRoom.ts constants
const TANK_RADIUS = 0.9;
const BULLET_RADIUS = 0.15;
const CIRCLE_SEGMENTS = 32;
const WIREFRAME_Y = 0.05; // slightly above ground to avoid z-fighting

export class DebugSystem {
  private app: pc.Application;
  /** Small on-screen label shown while debug mode is active. */
  private label: HTMLDivElement;

  enabled = false;

  constructor(app: pc.Application) {
    this.app = app;

    this.label = document.createElement('div');
    this.label.textContent = 'HITBOXES ON  [B]';
    this.label.style.cssText = [
      'position:fixed', 'top:48px', 'left:50%',
      'transform:translateX(-50%)',
      'padding:3px 12px',
      'background:rgba(0,0,0,0.55)',
      'color:#ffee44',
      'font-size:11px',
      'font-weight:700',
      'letter-spacing:0.14em',
      'border:1px solid rgba(255,238,68,0.35)',
      'border-radius:4px',
      'pointer-events:none',
      'z-index:50',
      'display:none',
    ].join(';');
    document.body.appendChild(this.label);
  }

  toggle(): boolean {
    this.enabled = !this.enabled;
    this.label.style.display = this.enabled ? 'block' : 'none';
    return this.enabled;
  }

  // ── Drawing primitives ───────────────────────────────────

  private drawCircle(cx: number, cz: number, radius: number, color: pc.Color) {
    const a = new pc.Vec3();
    const b = new pc.Vec3();
    for (let i = 0; i < CIRCLE_SEGMENTS; i++) {
      const t0 = (i / CIRCLE_SEGMENTS) * Math.PI * 2;
      const t1 = ((i + 1) / CIRCLE_SEGMENTS) * Math.PI * 2;
      a.set(cx + Math.cos(t0) * radius, WIREFRAME_Y, cz + Math.sin(t0) * radius);
      b.set(cx + Math.cos(t1) * radius, WIREFRAME_Y, cz + Math.sin(t1) * radius);
      this.app.drawLine(a, b, color, false);
    }
  }

  private drawBox(cx: number, cz: number, hw: number, hd: number, angle: number, color: pc.Color) {
    const cos = Math.cos(angle);
    const sin = Math.sin(angle);
    // Local corners rotated into world space
    const localCorners: [number, number][] = [[-hw, -hd], [hw, -hd], [hw, hd], [-hw, hd]];
    const corners = localCorners.map(([lx, lz]) =>
      new pc.Vec3(cx + lx * cos - lz * sin, WIREFRAME_Y, cz + lx * sin + lz * cos),
    );
    for (let i = 0; i < 4; i++) {
      this.app.drawLine(corners[i], corners[(i + 1) % 4], color, false);
    }
  }

  // ── Per-frame update ───────────────────────────────────────

  update(
    localX: number,
    localZ: number,
    remoteMachines: Map<string, MachineEntity>,
    destructibles: Map<string, DestructibleEntity>,
    bullets: BulletPool,
  ) {
    if (!this.enabled) return;

    const green  = new pc.Color(0.1, 1.0, 0.2);
    const red    = new pc.Color(1.0, 0.15, 0.1);
    const yellow = new pc.Color(1.0, 0.9, 0.1);
    const cyan   = new pc.Color(0.3, 0.9, 1.0);

    // ── Local machine — bright green circle ──────────────
    this.drawCircle(localX, localZ, TANK_RADIUS, green);

    // ── Remote machines — red circle ─────────────────────
    remoteMachines.forEach((machine) => {
      const pos = machine.root.getPosition();
      this.drawCircle(pos.x, pos.z, TANK_RADIUS, red);
    });

    // ── Destructibles — rotated box or circle depending on shape ─
    destructibles.forEach((ent) => {
      const def = DESTRUCTIBLE_DEFS[ent.type];
      const halfSize = def.scale * 0.5;
      const pos = ent.entity.getPosition();
      const isRound = ent.type === 'barrel' || ent.type === 'crystal_shard';

      if (isRound) {
        this.drawCircle(pos.x, pos.z, halfSize, yellow);
      } else {
        this.drawBox(pos.x, pos.z, halfSize, halfSize, ent.rotation, yellow);
      }
    });

    // ── Bullets — small cyan circle at current position ──
    bullets.forEachActive((bx, bz) => {
      this.drawCircle(bx, bz, BULLET_RADIUS, cyan);
    });
  }
}
